import { useEffect, useRef, useState } from 'react';
import {
  capabilityOfIssue,
  chemicalCases,
  chemicalEvidenceGroupOrder,
  chemicalIssues,
  chemicalSafetyNotice,
  chemicalSources,
  findChemicalCase,
  findChemicalSource,
  issuesForCapability,
  issuesSharingCapabilitySet,
  type ChemicalCapability,
  type ChemicalEvidenceGroup,
  type ChemicalIssue,
} from './chemical-research-content';
import type { DetailLocation } from './ScenarioDetailView';
import { ScenarioCardArt } from './ScenarioCardArt';
import { ChemicalResearchImage } from './ChemicalResearchImage';
import { ResearchInformationDrawer } from './ResearchInformationDrawer';
import type {
  ResearchDrawerCase,
  ResearchDrawerSource,
  ResearchInformationDetail,
} from './research-information';
import { handleScenarioTabsKeyDown } from './scenario-tab-keyboard';
import { modalDismissProps, useModalFocus } from './scenario-modal-focus';
import { platformRoutes } from '../../../shared/platform/platform-routes';

/**
 * 危化品事故详情页。
 *
 * 与高层建筑火灾、森林火灾共用同一套阅读结构：面包屑、场景头、两个页签、左侧问题列表、
 * 右侧单一内容面、能力说明抽屉。这里不接入源绑定投影，也不伪造一份 ScenarioProjection：
 * 内容来自 `chemical-research-content.ts` 的公开资料研究参考。
 *
 * 这一页只做四件事：说明问题、列出需要掌握的信息、说明哪些技术能力可以帮助获取信息、
 * 给出可追溯的官方依据与案例。它不创建 Mission、不生成处置指令、不给出任何阈值或距离。
 */
export function ChemicalScenarioDetailView({
  location,
  onLocation,
  onLibrary,
}: {
  readonly location: DetailLocation;
  readonly onLocation: (next: DetailLocation) => void;
  readonly onLibrary: () => void;
}) {
  const issue = chemicalIssues.find((row) => row.key === location.issue) ?? chemicalIssues[0];
  const [drawer, setDrawer] = useState<{
    readonly capability: ChemicalCapability;
    readonly issue: ChemicalIssue;
  } | null>(null);
  const [all, setAll] = useState(false);
  const [group, setGroup] = useState<ChemicalEvidenceGroup | null>(null);
  const [infoNeed, setInfoNeed] = useState<ChemicalIssue['information'][number] | null>(null);
  const readingScroll = useRef(0);
  const previousTab = useRef(location.tab);
  const opener = useRef<HTMLElement | null>(null);
  const infoOpener = useRef<HTMLElement | null>(null);
  const information = useRef<HTMLElement>(null);
  const capabilities = useRef<HTMLElement>(null);
  useEffect(() => {
    setDrawer(null);
    setInfoNeed(null);
    setAll(false);
    setGroup(null);
  }, [location.issue, location.tab]);
  useEffect(() => {
    if (location.tab !== 'issues' || !location.focus) return;
    (location.focus === 'information' ? information : capabilities).current?.scrollIntoView({
      block: 'start',
    });
  }, [location.issue, location.tab, location.focus]);
  useEffect(() => {
    if (previousTab.current === 'evidence' && location.tab === 'issues')
      window.scrollTo(0, readingScroll.current);
    if (previousTab.current === 'issues' && location.tab === 'evidence') window.scrollTo(0, 0);
    previousTab.current = location.tab;
  }, [location.tab]);
  if (!issue) return null;
  const changeTab = (tab: 'issues' | 'evidence') => {
    if (location.tab === 'issues' && tab === 'evidence') readingScroll.current = window.scrollY;
    onLocation({ ...location, tab, focus: null });
  };
  const related = capabilityOfIssue(issue);
  const sharedWith = issuesSharingCapabilitySet(issue);
  return (
    <section
      className="scc-detail scc-chem-detail"
      data-reference-nature="PUBLIC_RESEARCH_REFERENCE"
    >
      <nav className="scc-breadcrumb" aria-label="面包屑">
        <button onClick={onLibrary}>场景中心</button>
        <span aria-hidden="true">›</span>
        <strong>危化品事故</strong>
      </nav>
      <header className="scc-scene-head">
        <div className="scc-scene-art">
          <ScenarioCardArt id="chemical" name="危化品事故" />
        </div>
        <div className="scc-scene-title">
          <h1>危化品事故</h1>
          <p>物质身份、释放状态、人员暴露、火灾爆炸、影响范围与环境影响。</p>
        </div>
        <button className="scc-scene-back" onClick={onLibrary}>
          返回场景库 →
        </button>
      </header>
      <div
        className="scc-tab-bar"
        role="tablist"
        aria-label="场景详情"
        onKeyDown={handleScenarioTabsKeyDown}
      >
        {(['issues', 'evidence'] as const).map((tab) => (
          <button
            key={tab}
            role="tab"
            id={'scc-chem-tab-' + tab}
            aria-selected={location.tab === tab}
            aria-controls="scc-chem-panel"
            tabIndex={location.tab === tab ? 0 : -1}
            onClick={() => changeTab(tab)}
          >
            {tab === 'issues' ? '典型问题' : '依据与案例'}
          </button>
        ))}
      </div>
      <div id="scc-chem-panel" role="tabpanel" aria-labelledby={'scc-chem-tab-' + location.tab}>
        {location.tab === 'issues' ? (
          <div className="scc-issue-layout">
            <aside className="scc-issue-list" aria-label="典型问题">
              <h2>典型问题</h2>
              <ul>
                {chemicalIssues.map((row) => (
                  <li key={row.key}>
                    <button
                      data-issue={row.key}
                      aria-current={row.key === issue.key ? 'true' : undefined}
                      onClick={() => onLocation({ issue: row.key, tab: 'issues', focus: null })}
                    >
                      <span>{row.title}</span>
                      <span aria-hidden="true">›</span>
                    </button>
                  </li>
                ))}
              </ul>
              <p>选择问题，查看相关信息与能力。</p>
            </aside>
            <div className="scc-issue-main">
              <article className="scc-issue-intro">
                <div>
                  <h2>{issue.title}</h2>
                  <p>{issue.summary}</p>
                  <p className="scc-issue-impact">为什么需要关注：{issue.whyItMatters}</p>
                  <p className="scc-issue-impact scc-chem-unknown">待核实：{issue.unknowns}</p>
                </div>
                <ChemicalResearchImage file="00-chemical-incident-hero.png" label={issue.title} />
              </article>
              <section className="scc-read-section" ref={information} tabIndex={-1}>
                <div className="scc-heading-row">
                  <h2>需要掌握的信息</h2>
                  <button className="scc-text-button" onClick={() => changeTab('evidence')}>
                    查看相关依据 ↗
                  </button>
                </div>
                <p className="scc-section-lead">
                  以下是需要了解的信息，未表示当前已经取得；缺失的部分按“待核实”保留。
                </p>
                <ul className="scc-info-rows" data-linked-module="information">
                  {issue.information.map((row) => (
                    <li className="scc-info-row" key={row.name}>
                      <div className="scc-info-row-main">
                        <strong>{row.name}</strong>
                        <p>用途：{row.purpose}</p>
                        {row.detail && (
                          <p className="scc-info-expect">需要获取：{row.detail.what.join('、')}</p>
                        )}
                        <p>通常来源：{row.acquisition}</p>
                      </div>
                      <button
                        className="scc-text-button scc-info-evidence"
                        onClick={(event) => {
                          infoOpener.current = event.currentTarget;
                          setInfoNeed(row);
                        }}
                      >
                        查看依据 ↗
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
              <section className="scc-read-section" ref={capabilities} tabIndex={-1}>
                <h2>所需能力（研究主题）</h2>
                <p className="scc-section-lead">
                  这些技术能力可以帮助获取或核验信息；仅为研究关联、待专业审核，不代表本地已配备、已接入或获准使用。
                  本场景共整理 7 项能力研究主题，此处显示与当前问题相关的 {related.length} 项
                  {sharedWith.length > 0
                    ? `；这组主题与“${sharedWith.join('”“')}”相同，但每项在这两个问题里的用途不同。`
                    : '。'}
                </p>
                <ul className="scc-cap-grid scc-chem-cap-grid" data-linked-module="capability">
                  {related.map(({ capability, context }) => (
                    <li
                      className="scc-chem-card"
                      key={capability.key}
                      data-research-capability={capability.key}
                    >
                      <div className="scc-chem-card-media">
                        <ChemicalResearchImage file={capability.image} label={capability.title} />
                      </div>
                      <div className="scc-cap-body">
                        <strong>{capability.title}</strong>
                        <span className="scc-cap-scope" data-relation-marker>
                          研究关联 · 待专业审核
                        </span>
                        <p className="scc-cap-purpose">{context.purpose}</p>
                        <p className="scc-cap-output">可获得：{context.output}</p>
                        <button
                          className="scc-text-button"
                          onClick={(event) => {
                            opener.current = event.currentTarget;
                            setDrawer({ capability, issue });
                          }}
                        >
                          阅读能力说明 →
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
              <section className="scc-read-section">
                <h2>相关官方依据</h2>
                <ChemicalSources ids={issue.sourceIds} compact />
                <ChemicalCases ids={issue.caseIds} />
              </section>
              <footer className="scc-issue-footer">
                <span>所需能力仅为研究方向，尚无正式定义关联。</span>
                <button className="scc-primary" onClick={() => changeTab('evidence')}>
                  查看依据与案例
                </button>
              </footer>
            </div>
          </div>
        ) : (
          <section className="scc-evidence-page">
            <div className="scc-heading-row">
              <h2>依据与案例</h2>
              <div className="scc-evidence-switch" role="group" aria-label="资料范围">
                <button aria-pressed={!all} onClick={() => setAll(false)}>
                  与本问题相关
                </button>
                <button aria-pressed={all} onClick={() => setAll(true)}>
                  本场景全部资料
                </button>
              </div>
            </div>
            <p className="scc-section-lead">
              {all ? '危化品场景全部公开资料。' : `与“${issue.title}”相关的资料。`}
              先读摘要，再按需查阅官方原文；事故调查报告、事故摘要与演练报道分别标识。
            </p>
            <div className="scc-evidence-switch" role="group" aria-label="资料类型">
              {([null, ...chemicalEvidenceGroupOrder] as const).map((value) => (
                <button
                  key={value ?? 'all'}
                  aria-pressed={group === value}
                  onClick={() => setGroup(value)}
                >
                  {value ?? '全部类型'}
                </button>
              ))}
            </div>
            <ChemicalSources
              ids={all ? chemicalSources.map((row) => row.id) : issue.sourceIds}
              group={group}
            />
            {(!group || group === '事故调查报告' || group === '官方演练与实践') && (
              <ChemicalCases
                ids={all ? chemicalCases.map((row) => row.id) : issue.caseIds}
                group={group}
              />
            )}
            <button className="scc-text-button" onClick={() => changeTab('issues')}>
              返回当前问题：{issue.title} ↑
            </button>
          </section>
        )}
      </div>
      <p className="scc-notice scc-chem-safety">{chemicalSafetyNotice}</p>
      {drawer && (
        <ChemicalCapabilityDrawer
          capability={drawer.capability}
          issue={drawer.issue}
          restoreFocusTo={opener.current}
          onClose={() => setDrawer(null)}
        />
      )}
      {infoNeed && (
        <ResearchInformationDrawer
          sceneTitle="危化品事故"
          issueTitle={issue.title}
          needName={infoNeed.name}
          acquisition={infoNeed.acquisition}
          detail={chemicalInformationDetailOf(infoNeed)}
          sources={chemicalDrawerSources(chemicalInformationDetailOf(infoNeed))}
          cases={chemicalDrawerCases(issue)}
          restoreFocusTo={infoOpener.current}
          onClose={() => setInfoNeed(null)}
        />
      )}
    </section>
  );
}

type ChemicalScenarioNeed = ChemicalIssue['information'][number];

/** 缺少逐项详解时明确说明，不借用相邻条目，让“没有写”在页面上看得出来。 */
function chemicalInformationDetailOf(row: ChemicalScenarioNeed): ResearchInformationDetail {
  return (
    row.detail ?? {
      why: '这一项尚未编写逐项说明。',
      what: ['待内容 Owner 补充'],
      risk: '说明缺失时，读者无法判断这一项为何需要，也无法判断缺失会造成什么影响。',
      boundary: '在说明补齐之前，不展示推断出的解释。',
      sources: [],
    }
  );
}

export function chemicalDrawerSources(
  detail: ResearchInformationDetail,
): readonly ResearchDrawerSource[] {
  return detail.sources.flatMap((ref) => {
    const source = findChemicalSource(ref.id);
    if (!source) return [];
    return [
      {
        id: source.id,
        title: source.title,
        meta: `${source.kind} · ${source.publisher} · ${source.year}`,
        summary: source.summary,
        caveat: source.caveat,
        relation: ref.relation,
        url: source.url,
      },
    ];
  });
}

export function chemicalDrawerCases(issue: ChemicalIssue): readonly ResearchDrawerCase[] {
  return issue.caseIds.flatMap((id) => {
    const row = findChemicalCase(id);
    if (!row) return [];
    return [
      {
        id: row.id,
        title: row.title,
        meta: `${row.kind} · ${row.publisher} · ${row.year}`,
        summary: row.summary,
        caveat: row.caveat,
        url: row.url,
      },
    ];
  });
}

function issuesReferencingSource(id: string) {
  return chemicalIssues.filter((issue) => issue.sourceIds.includes(id)).map((row) => row.title);
}

function issuesReferencingCase(id: string) {
  return chemicalIssues.filter((issue) => issue.caseIds.includes(id)).map((row) => row.title);
}

export function ChemicalSources({
  ids,
  compact = false,
  group = null,
}: {
  readonly ids: readonly string[];
  readonly compact?: boolean;
  readonly group?: ChemicalEvidenceGroup | null;
}) {
  const rows = [...new Set(ids)].flatMap((id) => {
    const source = findChemicalSource(id);
    return source && (!group || source.group === group) ? [source] : [];
  });
  if (rows.length === 0)
    return (
      <p className="scc-chem-empty">
        此范围暂无该类型的法规或标准资料，可切换其他资料类型或查看本场景全部资料。
      </p>
    );
  const card = (row: (typeof rows)[number]) => (
    <article className="scc-evidence-card" data-evidence-id={row.id} key={row.id}>
      <span className="scc-chip">
        {row.kind} · {row.publisher} · {row.year}
      </span>
      {compact ? (
        <details>
          <summary>{row.title}</summary>
          {body(row)}
        </details>
      ) : (
        <>
          <h3>{row.title}</h3>
          {body(row)}
        </>
      )}
    </article>
  );
  const body = (row: (typeof rows)[number]) => (
    <>
      <p>{row.summary}</p>
      <p className="scc-evidence-use">{row.caveat}</p>
      <p className="scc-evidence-locator">关联问题：{issuesReferencingSource(row.id).join('、')}</p>
      <a href={row.url} target="_blank" rel="noopener noreferrer">
        打开官方原文 / PDF ↗
      </a>
    </>
  );
  if (compact)
    return (
      <div className="scc-evidence-grid" data-evidence-kind="source">
        {rows.map(card)}
      </div>
    );
  return (
    <div data-evidence-kind="source">
      {chemicalEvidenceGroupOrder.map((name) => {
        const grouped = rows.filter((row) => row.group === name);
        if (!grouped.length) return null;
        return (
          <section className="scc-evidence-group" data-evidence-group={name} key={name}>
            <h3>{name}</h3>
            {name === '法规与标准' && <ChemicalGroupNote group={name} />}
            <div className="scc-evidence-grid">{grouped.map(card)}</div>
          </section>
        );
      })}
    </div>
  );
}

export function ChemicalCases({
  ids,
  group = null,
}: {
  readonly ids: readonly string[];
  readonly group?: ChemicalEvidenceGroup | null;
}) {
  const rows = [...new Set(ids)].flatMap((id) => {
    const row = findChemicalCase(id);
    return row && (group === null || row.group === group) ? [row] : [];
  });
  if (rows.length === 0)
    return (
      <p className="scc-chem-empty">
        此范围暂无该类型的事故或演练资料，可切换其他资料类型或查看本场景全部资料。
      </p>
    );
  return (
    <div className="scc-chem-cases" data-evidence-kind="case" aria-label="事故案例与演练">
      <p className="scc-section-lead">
        事故调查报告、事故摘要与演练报道分别标识；摘要不是完整报告，演练不是真实事故。
      </p>
      {chemicalEvidenceGroupOrder.map((name) => {
        const grouped = rows.filter((row) => row.group === name);
        if (!grouped.length) return null;
        return (
          <section className="scc-evidence-group" data-evidence-group={name} key={name}>
            <h3>{name}</h3>
            {name === '事故调查报告' && <ChemicalGroupNote group={name} />}
            <div className="scc-evidence-grid">
              {grouped.map((row) => (
                <article className="scc-evidence-card" data-case-id={row.id} key={row.id}>
                  <span className="scc-chip">
                    {row.kind} · {row.publisher} · {row.year}
                  </span>
                  <h3>{row.title}</h3>
                  <p>{row.summary}</p>
                  <p className="scc-evidence-use">{row.caveat}</p>
                  <p className="scc-evidence-locator">
                    关联问题：{issuesReferencingCase(row.id).join('、')}
                  </p>
                  {row.url ? (
                    <a href={row.url} target="_blank" rel="noopener noreferrer">
                      打开官方原文 / PDF ↗
                    </a>
                  ) : (
                    <p className="scc-chem-unverified-link">
                      原文链接待核实：来源登记表未给出与该案例一一对应的官方页面，暂不提供外链。
                    </p>
                  )}
                </article>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

/**
 * 分组标题是阅读入口，不能比卡片上的资料类型说得更满。
 * 两组都明确写出组内到底放了什么，避免把事故摘要、事故警示资料或官方答复
 * 读成调查报告或正式标准。
 */
function ChemicalGroupNote({ group }: { readonly group: ChemicalEvidenceGroup }) {
  return (
    <p className="scc-evidence-group-note">
      {group === '法规与标准'
        ? '本组包含行政法规、行业标准、国家标准、征求意见稿与官方答复；每张卡片标注资料类型，征求意见稿与官方答复都不是正式标准。'
        : '本组包含事故调查报告，以及应急管理部发布的事故摘要与事故警示资料；每张卡片标注资料类型，摘要与警示资料不是完整调查报告。'}
    </p>
  );
}

export function ChemicalCapabilityDrawer({
  capability,
  issue,
  restoreFocusTo,
  onClose,
}: {
  readonly capability: ChemicalCapability;
  readonly issue: ChemicalIssue;
  readonly restoreFocusTo: HTMLElement | null;
  readonly onClose: () => void;
}) {
  const dialog = useModalFocus(restoreFocusTo);
  const context = issue.capabilities.find((row) => row.key === capability.key);
  const relatedIssues = issuesForCapability(capability.key);
  return (
    <dialog
      className="scc-drawer scc-capability-drawer scc-chem-drawer"
      ref={dialog}
      aria-modal="true"
      aria-labelledby="scc-chem-capability-title"
      {...modalDismissProps(onClose)}
    >
      <div className="scc-drawer-heading">
        <div>
          <p className="scc-drawer-eyebrow">研究说明 · 危化品事故 · {issue.title}</p>
          <h2 id="scc-chem-capability-title">{capability.title}</h2>
        </div>
        <button autoFocus onClick={onClose} aria-label="关闭">
          关闭 ×
        </button>
      </div>
      <div className="scc-drawer-body">
        <div className="scc-chem-drawer-hero">
          <ChemicalResearchImage file={capability.image} label={capability.title} />
        </div>
        <p className="scc-notice" data-capability-status="RESEARCH_TOPIC_ONLY">
          这是一项能力研究主题说明，不是正式 Capability 定义，也不表示设备已配备、已验证或已接入。
        </p>
        <section className="scc-drawer-section">
          <h3>它解决什么问题</h3>
          <p>{capability.problem}</p>
        </section>
        <section className="scc-drawer-section">
          <h3>在当前问题中做什么</h3>
          <p>{context?.purpose ?? capability.role}</p>
          {context && <p className="scc-drawer-note">可获得：{context.output}</p>}
        </section>
        <section className="scc-drawer-section">
          <h3>实际做什么</h3>
          <p>{capability.role}</p>
        </section>
        <div className="scc-chem-drawer-columns">
          <section className="scc-drawer-section">
            <h3>需要什么输入</h3>
            <ul className="scc-drawer-list">
              {capability.inputs.map((row) => (
                <li key={row}>{row}</li>
              ))}
            </ul>
          </section>
          <section className="scc-drawer-section">
            <h3>能提供什么输出</h3>
            <ul className="scc-drawer-list">
              {capability.outputs.map((row) => (
                <li key={row}>{row}</li>
              ))}
            </ul>
          </section>
        </div>
        <section className="scc-drawer-section">
          <h3>适用条件与限制</h3>
          <p>{capability.limits}</p>
        </section>
        <section className="scc-drawer-section">
          <h3>关联的典型问题</h3>
          <ul className="scc-drawer-list">
            {relatedIssues.map((title) => (
              <li key={title}>
                {title}
                {title === issue.title && <span className="scc-drawer-note"> · 当前问题</span>}
              </li>
            ))}
          </ul>
        </section>
        <section className="scc-drawer-section">
          <h3>官方依据与案例</h3>
          <ChemicalSources ids={capability.sourceIds} compact />
          {capability.caseIds.length > 0 && <ChemicalCases ids={capability.caseIds} />}
        </section>
        <p className="scc-drawer-footnote" data-definition-status="NO_APPROVED_MAPPING">
          能力定义暂不可查看：该研究主题尚无专业 Owner 批准的正式能力 ID 映射。
        </p>
        <a href={platformRoutes.equipment}>进入能力中心浏览／查找 →</a>
        <p className="scc-drawer-footnote">通用浏览入口，尚未精确匹配能力定义。</p>
      </div>
    </dialog>
  );
}
